import { Badge, Button, Card, EmptyState, Skeleton, formatDate } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listOccurrences, listRefundsDue, type ClassOccurrence } from "../../api/classes";
import { useServices } from "../../app/services";
import { addDays, minutesOfDay } from "../../lib/dates";
import k from "./classes.module.css";
import { Failure, styles as c, useIsWide, useScope, useStaffNames } from "./common";
import { OccurrenceDrawer } from "./OccurrenceDrawer";
import { fromMinutes, layoutLanes, toMinutes, weekDates } from "./time";

const PX = 1; // pixels per minute: 60px an hour

export function CalendarView() {
  const { client } = useServices();
  const sc = useScope();
  const wide = useIsWide();
  const { nameOf } = useStaffNames();
  const [anchor, setAnchor] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const day = anchor ?? sc.date;
  const dates = useMemo(() => (wide ? weekDates(day) : [day]), [wide, day]);
  const from = dates[0]!;
  const to = dates[dates.length - 1]!;
  const q = useQuery({ queryKey: ["classes", "occurrences", ...sc.key, from, to], queryFn: () => listOccurrences(client, { from, to, branchId: sc.branchId }).then((r) => r.occurrences), retry: false });
  const refunds = useQuery({ queryKey: ["classes", "refunds-due", ...sc.key], queryFn: () => listRefundsDue(client, { branchId: sc.branchId }).then((r) => r.refundsDue), retry: false });
  const step = wide ? 7 : 1;
  const opened = q.data?.find((o) => o.id === openId) ?? null;

  return (
    <div className={c.stack}>
      <div className={c.spread}>
        <div className={c.row}>
          <Button size="sm" aria-label={wide ? "Previous week" : "Previous day"} onClick={() => setAnchor(addDays(day, -step))}>‹</Button>
          <Button size="sm" onClick={() => setAnchor(null)}>Today</Button>
          <Button size="sm" aria-label={wide ? "Next week" : "Next day"} onClick={() => setAnchor(addDays(day, step))}>›</Button>
          <strong aria-live="polite">{wide ? `${formatDate(from, sc.ctx.locale, "short")} – ${formatDate(to, sc.ctx.locale, "short")}` : formatDate(day, sc.ctx.locale, "long")}</strong>
        </div>
        {(refunds.data?.length ?? 0) > 0 ? <Link to="/billing?tab=refunds"><Badge tone="warn">{refunds.data!.length} refund{refunds.data!.length === 1 ? "" : "s"} due → Billing</Badge></Link> : null}
      </div>
      <Card aria-label="Class calendar">
        {q.isPending ? <GridSkeleton cols={dates.length} /> : q.error ? <Failure error={q.error} title="Could not load the calendar" onRetry={() => void q.refetch()} /> : q.data.length === 0 ? (
          <EmptyState icon="dumbbell" title="No classes yet" description="Add a template to put classes on the calendar." action={sc.ctx.has("classes.template_add") ? <Button as={Link} to="/classes?tab=templates" variant="primary">Add a template</Button> : undefined} />
        ) : (
          <TimeGrid dates={dates} occurrences={q.data} today={sc.date} nowMin={minutesOfDay(sc.nowMs, sc.ctx.timeZone)} locale={sc.ctx.locale} nameOf={nameOf} onOpen={(o) => setOpenId(o.id)} />
        )}
      </Card>
      <OccurrenceDrawer occurrence={opened} trainerName={nameOf(opened?.trainerId)} onClose={() => setOpenId(null)} />
    </div>
  );
}

function GridSkeleton({ cols }: { cols: number }) {
  return (
    <div aria-busy="true" aria-label="Loading calendar" className={`${k.calBody} ${k.skGrid}`} style={{ ["--cols" as string]: cols }}>
      <div />
      {Array.from({ length: cols }, (_, i) => <div key={i} className={k.col}><Skeleton width="86%" height={44} /><Skeleton width="86%" height={64} /></div>)}
    </div>
  );
}

export function TimeGrid({ dates, occurrences, today, nowMin, locale, nameOf, onOpen }: {
  dates: string[]; occurrences: ClassOccurrence[]; today: string; nowMin: number; locale: string; nameOf: (id: string | null) => string; onOpen: (o: ClassOccurrence) => void;
}) {
  const starts = occurrences.map((o) => toMinutes(o.start));
  const ends = occurrences.map((o) => toMinutes(o.end));
  const first = Math.floor(Math.min(6 * 60, ...starts) / 60) * 60;
  const last = Math.ceil(Math.max(21 * 60, ...ends) / 60) * 60;
  const height = (last - first) * PX;
  const hours = Array.from({ length: (last - first) / 60 + 1 }, (_, i) => first + i * 60);
  return (
    <div className={k.cal} role="region" aria-label="Class time grid" tabIndex={0}>
      <div className={k.calHead} style={{ ["--cols" as string]: dates.length }}>
        <div className={k.gutter} />
        {dates.map((d) => <div key={d} className={`${k.dayHead} ${d === today ? k.today : ""}`}>{formatDate(d, locale, "weekday")}{d === today ? <Badge tone="info">Today</Badge> : null}</div>)}
      </div>
      <div className={k.calBody} style={{ ["--cols" as string]: dates.length, height }}>
        <div className={k.gutter}>{hours.slice(0, -1).map((h) => <span key={h} className={k.hour} style={{ top: (h - first) * PX }}>{fromMinutes(h)}</span>)}</div>
        {dates.map((d) => {
          const list = occurrences.filter((o) => o.date === d);
          return (
            <div key={d} className={k.col} data-date={d} role="group" aria-label={`Classes on ${d}`}>
              {hours.map((h) => <div key={h} className={k.line} style={{ top: (h - first) * PX }} />)}
              {d === today ? <div className={k.now} style={{ top: (nowMin - first) * PX }} aria-hidden="true" data-testid="now-line" /> : null}
              {layoutLanes(list, (o) => o).map(({ item: o, lane, lanes }) => {
                const full = o.status === "scheduled" && (o.seatsLeft ?? 1) === 0;
                const label = `${o.start} ${o.name}, ${o.seatsTaken ?? 0}/${o.capacity}${full ? ", full" : ""}${o.status === "cancelled" ? ", cancelled" : ""}`;
                return (
                  <button key={o.id} type="button" aria-label={label} title={`${o.name} · ${nameOf(o.trainerId)}`} onClick={() => onOpen(o)}
                    className={`${k.block} ${o.status === "cancelled" ? k.off : full ? k.full : o.taughtAt ? k.done : ""}`}
                    style={{ top: (toMinutes(o.start) - first) * PX, height: Math.max(24, (toMinutes(o.end) - toMinutes(o.start)) * PX - 2), left: `${(lane / lanes) * 100}%`, width: `calc(${100 / lanes}% - 2px)` }}>
                    <b>{o.start} {o.name}</b>
                    <span>{nameOf(o.trainerId)} · {o.seatsTaken ?? 0}/{o.capacity}{full ? " · Full" : ""}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
